import type { LucideIcon } from "lucide-react";
import {
    Briefcase,
    ClipboardList,
    FileSearch,
    Inbox,
    Mail,
    MessageSquare,
    Search,
    Send,
    Target,
    Zap,
} from "lucide-react";

export type MechanismRow = {
    icon: LucideIcon;
    title: string;
    body: string;
};

export type SignInMarketingContent = {
    eyebrow: string;
    headline: string;
    subhead?: string;
    mechanism: [MechanismRow, MechanismRow, MechanismRow];
};

const DEFAULT_CONTENT: SignInMarketingContent = {
    eyebrow: "Why this works",
    headline: "Job boards put you in a stack of 500. We put you in a stack of 1.",
    subhead:
        "Career Offensive is built for people who reach out instead of waiting in line.",
    mechanism: [
        {
            icon: Inbox,
            title: "Direct inbox",
            body: "Hiring managers read their own email. ATS doesn't.",
        },
        {
            icon: Send,
            title: "Your voice",
            body: "You write the message, from your own Gmail, with your name on it.",
        },
        {
            icon: MessageSquare,
            title: "Real conversation",
            body: "When they reply, you're talking. No re-application, no portal.",
        },
    ],
};

const FEATURE_CONTENT: Record<string, SignInMarketingContent> = {
    "contacts-lookup": {
        eyebrow: "Why this works",
        headline: "Skip the application portal. Email the person who hires.",
        subhead:
            "Direct contacts for the people who actually decide on the role.",
        mechanism: [
            {
                icon: Mail,
                title: "Verified emails",
                body: "Real inboxes for decision-makers, recruiters, and hiring managers.",
            },
            {
                icon: Search,
                title: "Built from real signals",
                body: "LinkedIn profiles, role context, and company structure — not a scrape.",
            },
            {
                icon: Send,
                title: "Outreach-ready",
                body: "Pulled into a draft you can send from your own inbox in minutes.",
            },
        ],
    },
    reports: {
        eyebrow: "How reports work",
        headline: "25 companies that fit you, ranked by how reachable they are.",
        subhead:
            "A targeted shortlist, not a thousand-row spreadsheet.",
        mechanism: [
            {
                icon: Target,
                title: "Mapped to your background",
                body: "Roles, industries, and seniority that line up with what you've done.",
            },
            {
                icon: Briefcase,
                title: "Decision-makers, not gatekeepers",
                body: "3–5 people per company who have real say over the hire.",
            },
            {
                icon: Zap,
                title: "Ready in minutes",
                body: "Run the report, scan the list, start your first message today.",
            },
        ],
    },
    "hub-interviews": {
        eyebrow: "What you'll get",
        headline: "Stop losing track of where you stand.",
        subhead:
            "Every interview, every company, in one place — so nothing slips.",
        mechanism: [
            {
                icon: ClipboardList,
                title: "One place per company",
                body: "Prep notes, recruiter contact, dates, follow-ups — together.",
            },
            {
                icon: MessageSquare,
                title: "Outcomes recorded",
                body: "What got asked, what worked, what you'd say differently next time.",
            },
            {
                icon: Target,
                title: "Pattern recognition",
                body: "See your full pipeline at a glance instead of guessing.",
            },
        ],
    },
    "resume-analysis": {
        eyebrow: "What you'll get",
        headline: "A line-by-line read of why your resume isn't getting calls.",
        subhead:
            "Honest scoring against the role you're chasing, not generic tips.",
        mechanism: [
            {
                icon: FileSearch,
                title: "Section-level scoring",
                body: "What's strong, what's weak, what's invisible to a hiring manager.",
            },
            {
                icon: Target,
                title: "Targeted to the role",
                body: "Analysis tied to the job you're chasing, not boilerplate advice.",
            },
            {
                icon: Zap,
                title: "Fix in place",
                body: "Apply guided edits and re-score without leaving the page.",
            },
        ],
    },
};

export function getSignInMarketingContent(
    slug?: string
): SignInMarketingContent {
    if (slug && FEATURE_CONTENT[slug]) {
        return FEATURE_CONTENT[slug];
    }
    return DEFAULT_CONTENT;
}
